import { useEffect, useRef, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from '@mui/material'
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer'
import Graphic from '@arcgis/core/Graphic'
import Point from '@arcgis/core/geometry/Point'
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol'
import { webMercatorToGeographic } from '@arcgis/core/geometry/support/webMercatorUtils'
import { CreateMap, CreateView } from '../MapComponents/EventMapSetup'
import DialogCloseButton from '../../../components/Buttons/DialogCloseButton'
import { useAppContext } from '../../../context/AppSettingsContextProvider'
import '../MapComponents/EventMapCSS.css'

interface CoordinatePickerDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (coords: { lat: number; lon: number }) => void
  initialLat?: number | null
  initialLon?: number | null
}

const PICKED_MARKER = new SimpleMarkerSymbol({
  color: [220, 50, 47],
  outline: { color: [255, 255, 255], width: 1.5 },
  size: 14,
})

function CoordinatePickerDialog({ open, onClose, onConfirm, initialLat, initialLon }: CoordinatePickerDialogProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const { isDarkTheme } = useAppContext()
  const [picked, setPicked] = useState<{ lat: number; lon: number } | null>(
    Number.isFinite(initialLat as number) && Number.isFinite(initialLon as number)
      ? { lat: initialLat as number, lon: initialLon as number }
      : null,
  )

  useEffect(() => {
    if (!open) return
    setPicked(
      Number.isFinite(initialLat as number) && Number.isFinite(initialLon as number)
        ? { lat: initialLat as number, lon: initialLon as number }
        : null,
    )

    const timer = setTimeout(() => {
      if (!mapRef.current) return

      const map = CreateMap(isDarkTheme)
      const view = CreateView(map, mapRef, isDarkTheme)

      const graphicsLayer = new GraphicsLayer()
      map.add(graphicsLayer)

      const placeMarker = (lat: number, lon: number) => {
        graphicsLayer.removeAll()
        const point = new Point({ longitude: lon, latitude: lat, spatialReference: { wkid: 4326 } })
        graphicsLayer.add(new Graphic({ geometry: point, symbol: PICKED_MARKER }))
      }

      if (Number.isFinite(initialLat as number) && Number.isFinite(initialLon as number)) {
        placeMarker(initialLat as number, initialLon as number)
        view.when(() => {
          view.center = new Point({
            longitude: initialLon as number,
            latitude: initialLat as number,
            spatialReference: { wkid: 4326 },
          })
          view.zoom = 14
        })
      }

      view.on('click', (e: any) => {
        if (!e.mapPoint) return
        const geographic = webMercatorToGeographic(e.mapPoint) as Point
        const lat = geographic.latitude as number | null | undefined
        const lon = geographic.longitude as number | null | undefined
        if (lat === null || lat === undefined || lon === null || lon === undefined) return
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return
        placeMarker(lat, lon)
        setPicked({ lat, lon })
      })
    }, 0)

    return () => clearTimeout(timer)
  }, [open, initialLat, initialLon, isDarkTheme])

  const handleConfirm = () => {
    if (picked) {
      onConfirm(picked)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={'lg'}
      fullWidth
      fullScreen={isSmall}
      slotProps={{
        paper: {
          sx: { height: '90vh', maxHeight: '90vh' },
        },
      }}
    >
      <DialogTitle>
        Velg posisjon i kart
        <DialogCloseButton handleClose={onClose} />
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Klikk i kartet for å sette posisjonen.
          {picked && ` Valgt: ${picked.lat.toFixed(6)}, ${picked.lon.toFixed(6)}`}
        </Typography>
        <Box ref={mapRef} sx={{ flex: 1, width: '100%', borderRadius: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button onClick={handleConfirm} disabled={!picked} variant="contained">
          Bruk posisjon
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CoordinatePickerDialog
